import React, {FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    Button,
    Container,
    Divider,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import {faChartBar} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import {
    ACard,
    AChartX,
    AToggleButtonGroup,
    AxisHoverParam,
    Period,
    PeriodSelector,
    Skeleton
} from "@atiautomacao/ati-ui-library";
import {format} from "date-fns";
import {useAppSelector} from "../../../../Config/Hooks";
import {RootState} from "../../../../Config/Store";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {useEquipmentNavigation} from "../../../../Shared/Hooks/useEquipmentNavigation";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import ChartBottomHint from "../../../../Shared/Components/ChartBottomHint/ChartBottomHint";
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import {MAX_PRESERVED_REFRESH_FAILURES} from "../../Performance/PerformanceRefreshPolicy";
import {fetchNcuEquipment, NcuEquipment, normalizeNcuDayPeriod, prepareNcuEquipment, TrackerRequestKind} from "./TrackersReducer";
import TrackerPositionHeatmap from "./TrackerPositionHeatmap";
import TrackerPositionHeatmapModal from "./TrackerPositionHeatmapModal";
import {
    createOptionDirection,
    createOptions,
    createOptionsSpeed,
    createTable,
    hasFiniteTrackerPoint,
    toTrackerPositionSeries
} from "./trackerPositionChartOptions";
import {TrackerRequestLimiter} from "./trackerRequestLimiter";
import {formatTrackerHintHeader, formatTrackerPosition, sortTrackerHintParams} from "./trackerPositionFormat";

export interface TrackerOption {
    id: number;
    name: string;
    nameSource: "navigation" | "fallback";
}

interface TrackerPositionContext {
    contextId: string;
    ncuId: number;
    periodKey: string;
}

interface TrackerRequestAttempt {
    requestId: string;
    contextId: string;
    kind: TrackerRequestKind;
    controller: AbortController;
}

export type TrackerPositionViewMode = "curves" | "heatmap";

// Nomes de tracker são curtos (TK-001: +12,34): colunas mais estreitas que o padrão do hint.
const TRACKER_HINT_ITEM_MIN_WIDTH = 130;
const TRACKER_HINT_COLUMN_GAP = 8;
// NCUs com muitos trackers: limita a altura do hint (rolagem interna) para não empurrar o restante do card.
const TRACKER_HINT_MAX_HEIGHT = 250;

interface TrackerPositionSnapshot extends NcuEquipment {
    contextId: string;
    period: Period;
}

interface TrackerPositionView {
    contextId: string | null;
    status: "idle" | "loading" | "data" | "empty" | "error";
    snapshot: TrackerPositionSnapshot | null;
    refreshing: boolean;
    pendingRequestId: string | null;
}

type DetailResult =
    | {ok: true; data: any}
    | {ok: false; error: any};

const emptyPositionView = (): TrackerPositionView => ({
    contextId: null,
    status: "idle",
    snapshot: null,
    refreshing: false,
    pendingRequestId: null
});

export interface NcuPositionHistoryCardProps {
    powerPlantId: number;
    ncu: TrackerOption;
    title: string;
    modalTitle: string;
    ariaLabel: string;
    period: Period;
    onPeriodChange: (period: Period) => void;
    onOpenCommands: (ncuId: number) => void;
    requestLimiter: TrackerRequestLimiter;
    /** When provided, the visualization mode is controlled by the parent. */
    positionMode?: TrackerPositionViewMode;
    onPositionModeChange?: (mode: TrackerPositionViewMode) => void;
}

const NcuPositionHistoryCard: FunctionComponent<NcuPositionHistoryCardProps> = ({
    powerPlantId,
    ncu,
    title,
    modalTitle,
    ariaLabel,
    period,
    onPeriodChange,
    onOpenCommands,
    requestLimiter,
    positionMode: controlledPositionMode,
    onPositionModeChange
}) => {
    const theme = useTheme();
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const navigateToEquipment = useEquipmentNavigation();
    const [positionContext, setPositionContext] = useState<TrackerPositionContext | null>(null);
    const [positionView, setPositionView] = useState<TrackerPositionView>(emptyPositionView);
    const [localPositionMode, setLocalPositionMode] = useState<TrackerPositionViewMode>("curves");
    const positionMode = controlledPositionMode ?? localPositionMode;
    const [isHeatmapModalOpen, setIsHeatmapModalOpen] = useState(false);
    // Hint exibido abaixo do gráfico de curvas (SGD-3844), alimentado pelo onAxisHover do AChartX.
    const [hintParams, setHintParams] = useState<AxisHoverParam[] | null>(null);
    const handleAxisHover = useCallback((params: AxisHoverParam[] | null) => {
        setHintParams(sortTrackerHintParams(params));
    }, []);

    const normalizedPeriod = useMemo(() => normalizeNcuDayPeriod(period), [period]);
    const periodKey = `${LocalDateTimeFormatISO(normalizedPeriod.fromDateTime)}|${LocalDateTimeFormatISO(normalizedPeriod.toDateTime)}`;
    const ncuId = ncu.id;
    const activeContextRef = useRef<TrackerPositionContext | null>(null);
    const activeAttemptRef = useRef<TrackerRequestAttempt | null>(null);
    const consecutiveFailuresRef = useRef(0);
    const positionCardRef = useRef<HTMLDivElement | null>(null);
    const heatmapReturnFocusRef = useRef<HTMLButtonElement | null>(null);
    const pendingHeatmapFocusRestoreRef = useRef(false);
    const contextSequenceRef = useRef(0);
    const requestSequenceRef = useRef(0);
    const positionViewRef = useRef(positionView);
    const periodKeyRef = useRef(periodKey);
    periodKeyRef.current = periodKey;
    const normalizedPeriodRef = useRef(normalizedPeriod);
    normalizedPeriodRef.current = normalizedPeriod;

    const updatePositionView = useCallback((updater: (current: TrackerPositionView) => TrackerPositionView) => {
        const next = updater(positionViewRef.current);
        positionViewRef.current = next;
        setPositionView(next);
    }, []);

    const abortActiveAttempt = useCallback(() => {
        const attempt = activeAttemptRef.current;
        activeAttemptRef.current = null;
        attempt?.controller.abort();
    }, []);

    const handleRefreshFailure = useCallback((context: TrackerPositionContext) => {
        consecutiveFailuresRef.current += 1;
        if (consecutiveFailuresRef.current > MAX_PRESERVED_REFRESH_FAILURES) {
            updatePositionView(() => ({
                contextId: context.contextId,
                status: "empty",
                snapshot: null,
                refreshing: false,
                pendingRequestId: null
            }));
            return;
        }
        updatePositionView(current => ({
            ...current,
            refreshing: false,
            pendingRequestId: null
        }));
    }, [updatePositionView]);

    const requestNcuDetail = useCallback((
        context: TrackerPositionContext,
        kind: TrackerRequestKind,
        requestPeriod: Period
    ) => {
        if (activeAttemptRef.current ||
            activeContextRef.current?.contextId !== context.contextId ||
            periodKeyRef.current !== context.periodKey) {
            return;
        }

        const controller = new AbortController();
        const requestId = `${context.contextId}-request-${++requestSequenceRef.current}`;
        activeAttemptRef.current = {requestId, contextId: context.contextId, kind, controller};

        updatePositionView(current => kind === "refresh" ? {
            ...current,
            contextId: context.contextId,
            refreshing: true,
            pendingRequestId: requestId
        } : {
            contextId: context.contextId,
            status: "loading",
            snapshot: null,
            refreshing: false,
            pendingRequestId: requestId
        });

        const handleResult = (result: DetailResult) => {
            const currentContext = activeContextRef.current;
            const currentAttempt = activeAttemptRef.current;
            const ownsResult = currentContext?.contextId === context.contextId &&
                currentContext.ncuId === context.ncuId &&
                currentContext.periodKey === context.periodKey &&
                currentAttempt?.requestId === requestId &&
                periodKeyRef.current === context.periodKey;

            if (!ownsResult) {
                return;
            }

            activeAttemptRef.current = null;
            const responseData = result.ok ? result.data : null;
            const responseHasAnotherId = responseData?.id !== undefined && Number(responseData.id) !== context.ncuId;
            const validNoData = result.ok
                ? !responseData
                : Number(result.error?.response?.data?.id) === context.ncuId;

            if (responseHasAnotherId) {
                if (kind === "refresh") {
                    handleRefreshFailure(context);
                    return;
                }
                updatePositionView(() => ({
                    contextId: context.contextId,
                    status: "error",
                    snapshot: null,
                    refreshing: false,
                    pendingRequestId: null
                }));
                return;
            }

            if (validNoData) {
                consecutiveFailuresRef.current = 0;
                updatePositionView(() => ({
                    contextId: context.contextId,
                    status: "empty",
                    snapshot: null,
                    refreshing: false,
                    pendingRequestId: null
                }));
                return;
            }

            if (result.ok && responseData) {
                consecutiveFailuresRef.current = 0;
                const prepared = prepareNcuEquipment(responseData);
                const hasData = hasFiniteTrackerPoint(prepared.chart);
                updatePositionView(() => ({
                    contextId: context.contextId,
                    status: hasData ? "data" : "empty",
                    snapshot: hasData ? {...prepared, contextId: context.contextId, period: requestPeriod} : null,
                    refreshing: false,
                    pendingRequestId: null
                }));
                return;
            }

            if (kind === "refresh") {
                handleRefreshFailure(context);
                return;
            }
            updatePositionView(() => ({
                contextId: context.contextId,
                status: "error",
                snapshot: null,
                refreshing: false,
                pendingRequestId: null
            }));
        };

        requestLimiter.run(() => fetchNcuEquipment({
            powerPlantId,
            ncuId: context.ncuId,
            period: requestPeriod,
            signal: controller.signal
        }), controller.signal)
            .then(response => handleResult({ok: true, data: response?.data?.data}))
            .catch(error => handleResult({ok: false, error}));
    }, [handleRefreshFailure, powerPlantId, requestLimiter, updatePositionView]);

    useLayoutEffect(() => {
        consecutiveFailuresRef.current = 0;
        const context: TrackerPositionContext = {
            contextId: `tracker-position-${ncuId}-${++contextSequenceRef.current}`,
            ncuId,
            periodKey
        };
        activeContextRef.current = context;
        setPositionContext(context);
        positionViewRef.current = {
            contextId: context.contextId,
            status: "loading",
            snapshot: null,
            refreshing: false,
            pendingRequestId: null
        };
        setPositionView(positionViewRef.current);
        requestNcuDetail(context, "initial", normalizedPeriodRef.current);

        return () => {
            abortActiveAttempt();
            activeContextRef.current = null;
        };
    }, [abortActiveAttempt, ncuId, periodKey, requestNcuDetail]);

    useInterval(() => {
        const context = activeContextRef.current;
        if (!context || activeAttemptRef.current || periodKeyRef.current !== context.periodKey) {
            return;
        }
        const status = positionViewRef.current.status;
        const kind: TrackerRequestKind = status === "data" || status === "empty" || status === "error"
            ? "refresh"
            : "initial";
        requestNcuDetail(context, kind, normalizedPeriodRef.current);
    }, userConfig.mainUpdateInterval);

    const handleNcuPeriodChange = (newPeriod: Period) => {
        const normalized = normalizeNcuDayPeriod(newPeriod);
        const normalizedKey = `${LocalDateTimeFormatISO(normalized.fromDateTime)}|${LocalDateTimeFormatISO(normalized.toDateTime)}`;
        if (normalizedKey === periodKey) {
            return;
        }
        onPeriodChange(normalized);
    };

    const handlePositionModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        nextMode: string | null
    ) => {
        if (nextMode === "curves" || nextMode === "heatmap") {
            setLocalPositionMode(nextMode);
            onPositionModeChange?.(nextMode);
        }
    };

    const findPositionCardActionsButton = useCallback(() => (
        positionCardRef.current?.querySelector<HTMLButtonElement>(
            'button[aria-haspopup="true"]'
        ) ?? null
    ), []);

    const handleOpenHeatmapModal = () => {
        heatmapReturnFocusRef.current = findPositionCardActionsButton();
        pendingHeatmapFocusRestoreRef.current = false;
        setIsHeatmapModalOpen(true);
    };

    const handleCloseHeatmapModal = useCallback(() => {
        pendingHeatmapFocusRestoreRef.current = true;
        setIsHeatmapModalOpen(false);
    }, []);

    const restoreHeatmapFocus = useCallback(() => {
        const currentActionsButton = findPositionCardActionsButton();
        const previousActionsButton = heatmapReturnFocusRef.current;
        const target = currentActionsButton ?? (
            previousActionsButton?.isConnected ? previousActionsButton : null
        );

        if (target) {
            target.focus();
            heatmapReturnFocusRef.current = target;
            pendingHeatmapFocusRestoreRef.current = false;
        }
    }, [findPositionCardActionsButton]);

    const ncuPeriod = useMemo((): Period => ({
        groupBy: "day",
        fromDateTime: normalizedPeriod.fromDateTime,
        toDateTime: normalizedPeriod.toDateTime
    }), [normalizedPeriod]);

    const hasCurrentPositionContext = positionContext?.ncuId === ncuId &&
        positionContext.periodKey === periodKey &&
        positionView.contextId === positionContext.contextId;
    const visibleSnapshot = hasCurrentPositionContext && positionView.status === "data"
        ? positionView.snapshot
        : null;
    const visiblePositionSeries = useMemo(
        () => toTrackerPositionSeries(visibleSnapshot?.chart),
        [visibleSnapshot]
    );
    const curvesOption = useMemo(
        () => visibleSnapshot ? createOptions(visibleSnapshot.chart) : null,
        [visibleSnapshot]
    );
    const visibleContextId = visibleSnapshot?.contextId ?? null;
    useEffect(() => {
        // Troca de NCU/dia ou de visualização invalida o ponto selecionado.
        setHintParams(null);
    }, [visibleContextId, positionMode]);
    const speedOption = useMemo(
        () => visibleSnapshot ? createOptionsSpeed(visibleSnapshot) : null,
        [visibleSnapshot]
    );
    const directionOption = useMemo(
        () => visibleSnapshot ? createOptionDirection(visibleSnapshot) : null,
        [visibleSnapshot]
    );

    useEffect(() => {
        if (!isHeatmapModalOpen &&
            pendingHeatmapFocusRestoreRef.current &&
            hasCurrentPositionContext &&
            positionView.status === "data") {
            restoreHeatmapFocus();
        }
    }, [
        hasCurrentPositionContext,
        isHeatmapModalOpen,
        positionView.status,
        restoreHeatmapFocus
    ]);

    const emptyState = (
        <Container fixed>
            <Box sx={{bgcolor: '#e1e0e0', height: '500px', width: '100%'}}>
                <DataNotFound
                    boxStyle={{height: '500px', width: '100%'}}
                    titleStyle={{fontSize: '23px', color: '#646363'}}
                />
            </Box>
        </Container>
    );
    const errorState = (
        <Container fixed>
            <Box sx={{bgcolor: '#e1e0e0', height: '500px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography sx={{fontSize: '23px', color: '#646363'}}>
                    Não foi possível carregar os dados.
                </Typography>
            </Box>
        </Container>
    );

    const isLoading = !hasCurrentPositionContext ||
        positionView.status === "loading" ||
        positionView.status === "idle";
    const accordionId = `tracker-position-${powerPlantId}-${ncuId}`;

    return (
        <>
            {isLoading ? (
                <Skeleton animation="wave" variant="rounded" height={800} width="100%"/>
            ) : (
                <div
                    key={`${powerPlantId}-${ncuId}-${periodKey}`}
                    className="tracker-position-card-shell"
                >
                    <ACard
                        cardRef={positionCardRef}
                        key={`chart-power-trackers-${openSubMenu}-${powerPlantId}-${ncuId}-${periodKey}`}
                        title={title}
                        subTitle={(
                            <span className="tracker-position-subtitle">
                                Para uma visualização completa, clique no botão
                                <MoreHorizOutlinedIcon
                                    className="tracker-position-subtitle-more-icon"
                                    aria-hidden="true"
                                /> Ver mais, e selecione
                                <FullscreenOutlinedIcon
                                    className="tracker-position-subtitle-expand-icon"
                                    aria-hidden="true"
                                /> Modo expandido.
                            </span>
                        ) as unknown as string}
                        ariaLabel={ariaLabel}
                        iconTitle={{
                            showIcon: true,
                            kind: "mui",
                            iconMaterialComponent: ShareLocationIcon,
                            iconTitleStyle: {
                                width: "35px",
                                height: "35px",
                                padding: "3px"
                            }
                        }}
                        styleProps={{
                            cardStyle: {
                                borderRadius: "8px"
                            },
                            headerStyle: {
                                padding: "12px 8px 8px"
                            },
                            cardTitleStyle: {
                                gap: "8px",
                                padding: "8px"
                            },
                            titleStyle: {
                                fontFamily: theme.typography.fontFamily,
                                fontSize: "20px",
                                fontWeight: 500,
                                lineHeight: 1.6,
                                letterSpacing: "0.15px"
                            },
                            subTitleStyle: {
                                padding: "0 8px 8px",
                                marginTop: "4px",
                                fontFamily: theme.typography.fontFamily,
                                fontSize: "14px",
                                lineHeight: 1.43,
                                letterSpacing: "0.17px",
                                color: theme.palette.text.primary
                            },
                            contentStyle: {
                                display: "flex",
                                flexDirection: "column",
                                padding: positionMode === "heatmap" ? "0 16px 12px" : "16px"
                            }
                        }}
                        headerControlsPosition={"header"}
                        headerControls={
                            <Box
                                className="tracker-position-header-controls"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <AToggleButtonGroup
                                    title="Visualização da posição"
                                    options={[
                                        {
                                            value: "curves",
                                            label: "Gráfico",
                                            ariaLabel: "Gráfico"
                                        },
                                        {
                                            value: "heatmap",
                                            label: "Mapa de calor",
                                            ariaLabel: "Mapa de calor"
                                        }
                                    ]}
                                    value={positionMode}
                                    exclusive
                                    onChange={handlePositionModeChange}
                                    styleProps={{
                                        toggleButtonGroup: {
                                            width: "204px",
                                            height: "38px",
                                            borderRadius: "8px"
                                        },
                                        toggleButton: {
                                            height: "38px",
                                            padding: "8px"
                                        },
                                        typography: {
                                            fontFamily: theme.typography.fontFamily,
                                            fontWeight: 500,
                                            fontSize: "13px",
                                            lineHeight: "22px",
                                            letterSpacing: "0.46px",
                                            textTransform: "uppercase"
                                        }
                                    }}
                                />
                                <Box className="tracker-position-day-control">
                                    <span className="tracker-position-day-label">Dia</span>
                                    <span className="tracker-position-day-value">
                                        {format(ncuPeriod.toDateTime, "dd/MM/yyyy")}
                                    </span>
                                    <PeriodSelector
                                        key={periodKey}
                                        styleProps={{
                                            base: {
                                                display: "flex",
                                                alignItems: "center"
                                            }
                                        }}
                                        mode="button"
                                        hideDatetime={false}
                                        hideGroup
                                        disableHourSelector
                                        inputFormat="dd/MM/yyyy"
                                        periodInterval={{
                                            day: {
                                                date: ncuPeriod.toDateTime,
                                                startTime: "04:00:00",
                                                endTime: "21:00:00"
                                            }
                                        }}
                                        period={ncuPeriod}
                                        onChange={handleNcuPeriodChange}
                                        groupByOptions={["day"]}
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    size={"small"}
                                    onClick={() => onOpenCommands(ncuId)}
                                    endIcon={<SendIcon/>}
                                    sx={{
                                        minWidth: "109.81px",
                                        height: "34px",
                                        borderRadius: "4px",
                                        backgroundColor: "#2959eb",
                                        textTransform: "capitalize",
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        letterSpacing: "0.371px",
                                        lineHeight: "22.75px"
                                    }}
                                >
                                    Comandos
                                </Button>
                            </Box>
                        }

                        headerActions={
                            <Stack direction="column" spacing="8px">
                                <MenuItem
                                    autoFocus
                                    onClick={handleOpenHeatmapModal}
                                    sx={{borderRadius: "4px"}}
                                >
                                    <ListItemIcon>
                                        <FullscreenOutlinedIcon fontSize="small"/>
                                    </ListItemIcon>
                                    <ListItemText>Modo expandido</ListItemText>
                                </MenuItem>
                                <Divider/>
                                <MenuItem
                                    disabled={!visibleSnapshot}
                                    onClick={() => visibleSnapshot && navigateToEquipment(visibleSnapshot.id)}
                                    sx={{borderRadius: "4px"}}
                                >
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faChartBar}/>
                                    </ListItemIcon>
                                    <ListItemText>Equipamento</ListItemText>
                                </MenuItem>
                            </Stack>
                        }
                        InformationTooltip={
                            <Box
                                className="tracker-position-info"
                                aria-label="Informações do histórico de posicionamento"
                            >
                                <InfoOutlinedIcon/>
                            </Box>
                        }
                    >
                        {
                            visibleSnapshot ?
                                positionMode === "heatmap" ? (
                                    <Box className="tracker-position-heatmap-view">
                                        <TrackerPositionHeatmap
                                            contextId={visibleSnapshot.contextId}
                                            series={visiblePositionSeries}
                                            period={{
                                                start: visibleSnapshot.period.fromDateTime,
                                                end: visibleSnapshot.period.toDateTime
                                            }}
                                            isMobile={window.innerWidth < 771}
                                        />
                                    </Box>
                                ) : (
                                <>
                                    <Box className="ncuGraphs">
                                        <Box className="windStyle">
                                            <Box sx={{width: "100%"}}>
                                                <AChartX
                                                    option={speedOption}
                                                    height={175}
                                                    loading={false}
                                                    style={{marginTop: '-2%'}}
                                                    theme={theme.palette.mode}
                                                />
                                                <Typography style={{marginLeft: '22%', marginTop: '-5%'}} variant="subtitle1" fontSize={12} color="text.primary">Velocidade do Vento</Typography>
                                            </Box>
                                            <Box sx={{width: "100%"}}>
                                                <AChartX
                                                    option={directionOption}
                                                    height={175}
                                                    loading={false}
                                                    style={{marginTop: '-2%'}}
                                                    theme={theme.palette.mode}
                                                />
                                                <Typography style={{marginLeft: '25%', marginTop: '2%'}} variant="subtitle1" fontSize={12} color="text.primary">Direção do Vento</Typography>
                                            </Box>
                                        </Box>
                                        <Box className="tracker-position-view">
                                            <Box data-testid="tracker-position-curves">
                                                <AChartX
                                                    key={`ncu-line-chart-${ncuId}-${periodKey}`}
                                                    option={curvesOption}
                                                    width={window.innerWidth < 1050 ? "123%" : "112%"}
                                                    height={450}
                                                    loading={false}
                                                    style={{marginTop: '2%', marginLeft: window.innerWidth < 1050 ? '0%' : '-9%'}}
                                                    theme={theme.palette.mode}
                                                    onAxisHover={handleAxisHover}
                                                />
                                            </Box>
                                            <ChartBottomHint
                                                params={hintParams}
                                                formatHeader={formatTrackerHintHeader}
                                                formatValue={param => formatTrackerPosition(param.value)}
                                                itemMinWidth={TRACKER_HINT_ITEM_MIN_WIDTH}
                                                columnGap={TRACKER_HINT_COLUMN_GAP}
                                                sx={{mb: 2, maxHeight: TRACKER_HINT_MAX_HEIGHT, overflowY: 'auto'}}
                                            />
                                        </Box>
                                    </Box>
                                    <Accordion style={{marginTop: 0}}>
                                        <AccordionSummary
                                            expandIcon={<ArrowDropDownIcon/>}
                                            aria-controls={`${accordionId}-content`}
                                            id={`${accordionId}-header`}
                                        >
                                            <Typography>Trackers</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {createTable(visibleSnapshot.trackers)}
                                        </AccordionDetails>
                                    </Accordion>
                                </>
                                )
                            :
                            positionView.status === "error" ? errorState : emptyState
                        }
                    </ACard>
                </div>
            )}
            <TrackerPositionHeatmapModal
                open={isHeatmapModalOpen}
                onClose={handleCloseHeatmapModal}
                onExited={restoreHeatmapFocus}
                title={modalTitle}
                period={ncuPeriod}
                onPeriodChange={handleNcuPeriodChange}
                contextId={hasCurrentPositionContext ? positionContext?.contextId ?? null : null}
                status={hasCurrentPositionContext ? positionView.status : "loading"}
                refreshing={hasCurrentPositionContext && positionView.refreshing}
                series={visiblePositionSeries}
                isMobile={window.innerWidth < 771}
            />
        </>
    );
};

export default React.memo(NcuPositionHistoryCard);
